import type React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronsUpDown } from "lucide-react";
import {
	SidebarMenu,
	SidebarMenuItem,
	SidebarMenuButton,
} from "@/components/ui/sidebar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState } from "react";
import type { User as UserType } from "@/types/user";
import { fetchUserProfile } from "@/services/userService";
import { Skeleton } from "@/components/ui/skeleton";
import {
	User,
	Settings,
	Bell,
	LogOut,
	HelpCircle,
	MessageSquare,
} from "lucide-react";

const UserProfile: React.FC = () => {
	const { user: authUser, logout } = useAuth();
	const navigate = useNavigate();
	const [userProfile, setUserProfile] = useState<UserType | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const loadUserProfile = async () => {
			if (!authUser?.id) return;

			try {
				const profile = await fetchUserProfile(authUser.id);
				setUserProfile(profile);
			} catch (error) {
				console.error("プロフィール取得エラー:", error);
			} finally {
				setLoading(false);
			}
		};

		loadUserProfile();
	}, [authUser?.id]);

	const handleLogout = async () => {
		try {
			await logout();
			navigate("/login");
		} catch (error) {
			console.error("ログアウトエラー:", error);
		}
	};

	// ユーザー名のイニシャルを取得
	const getInitials = (name?: string) => {
		if (!name) return "UN";
		return name
			.split(" ")
			.map((n) => n[0])
			.join("")
			.toUpperCase()
			.slice(0, 2);
	};

	if (loading) {
		return (
			<SidebarMenu>
				<SidebarMenuItem>
					<SidebarMenuButton size="lg">
						<Skeleton className="h-8 w-8 rounded-lg" />
						<div className="flex-1 ml-3">
							<Skeleton className="h-4 w-24 mb-1" />
							<Skeleton className="h-3 w-32" />
						</div>
					</SidebarMenuButton>
				</SidebarMenuItem>
			</SidebarMenu>
		);
	}

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							size="lg"
							className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
						>
							<Avatar className="h-8 w-8 rounded-lg">
								<AvatarImage
									src={userProfile?.avatarUrl}
									alt={userProfile?.username || "ユーザー"}
								/>
								<AvatarFallback>
									{getInitials(userProfile?.username)}
								</AvatarFallback>
							</Avatar>
							<div className="truncate grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]/sidebar:hidden">
								<span className="truncate font-semibold">
									{userProfile?.username || "未設定"}
								</span>
								<span className="truncate text-xs">
									{userProfile?.email || "メールアドレス未設定"}
								</span>
							</div>
							<ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]/sidebar:hidden" />
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
						side="top"
						align="end"
						sideOffset={4}
					>
						{/* アカウント関連 */}
						<DropdownMenuItem
							onClick={() => navigate("/webapp/settings/profile")}
						>
							<User className="mr-2 h-4 w-4" />
							プロフィール設定
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={() => navigate("/webapp/settings/account")}
						>
							<Settings className="mr-2 h-4 w-4" />
							アカウント設定
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={() => navigate("/webapp/settings/notifications")}
						>
							<Bell className="mr-2 h-4 w-4" />
							通知設定
						</DropdownMenuItem>

						<DropdownMenuSeparator />

						{/* サポート関連 */}
						<DropdownMenuItem onClick={() => navigate("/webapp/help")}>
							<HelpCircle className="mr-2 h-4 w-4" />
							ヘルプ
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => navigate("/webapp/feedback")}>
							<MessageSquare className="mr-2 h-4 w-4" />
							フィードバック
						</DropdownMenuItem>

						<DropdownMenuSeparator />

						{/* ログアウト */}
						<DropdownMenuItem
							onClick={handleLogout}
							className="text-destructive focus:text-destructive"
						>
							<LogOut className="mr-2 h-4 w-4" />
							ログアウト
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	);
};

export default UserProfile;
